<script>
import { QCalendarMonth, parseDate } from '@quasar/quasar-ui-qcalendar'
import Modal from '@/components/admin/logis/modal/ScheduleUnit.vue'

const TODAY = parseDate(new Date()).date

const PARTNER_CATEGORY_LABELS = {
  outbound: '출고 예정',
  inbound: '입고 예정',
  promotion: '프로모션',
  stock: '재고 준비',
  offline: '응대 불가',
  etc: '기타'
}

const AONE_CATEGORY_LABELS = {
  meeting: '회의',
  fieldwork: '현장 작업',
  closing: '마감',
  visit: '방문 일정',
  leave: '휴가/부재',
  training: '교육',
  etc: '기타'
}

export default {
  name: 'AdminScheduler',
  components: {
    Modal,
    QCalendarMonth
  },
  data () {
    return {
      modalVisibility: false,
      modalMode: 'add',
      calDate: new Date(),
      selectedDate: TODAY,
      selectedSchedule: {},
      events: [],
      scheduleScopeFilter: 'all',
      partnerIdFilter: null,
      statusFilter: 'all',
      visibilityFilter: 'all',
      searchText: ''
    }
  },
  computed: {
    session () {
      return this.$store.getters.getLocalSession || {}
    },
    authLevel () {
      return Number(this.session.authLevel || 0)
    },
    isAdmin () {
      return this.authLevel >= 90
    },
    isPartner () {
      return this.authLevel === 80
    },
    scheduleScopeOptions () {
      const base = [
        { label: '전체 일정', value: 'all' },
        { label: '자동 운송 일정', value: 'console' },
        { label: '파트너 일정', value: 'partner' }
      ]
      if (this.isAdmin) {
        base.push({ label: 'AONE 내부 일정', value: 'aone' })
      }
      return base
    },
    statusOptions () {
      return [
        { label: '전체 상태', value: 'all' },
        { label: '예정', value: 'planned' },
        { label: '진행 중', value: 'in_progress' },
        { label: '완료', value: 'done' },
        { label: '보류', value: 'on_hold' }
      ]
    },
    visibilityOptions () {
      return [
        { label: '전체 공개 범위', value: 'all' },
        { label: '개인 일정', value: 'private' },
        { label: '내부 공유', value: 'shared' }
      ]
    },
    partnerFilterOptions () {
      return [{ label: '전체 파트너', value: null }].concat(this.$store.getters.getPartnerList || [])
    },
    currentMonthLabel () {
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: 'long'
      }).format(this.calDate)
    },
    filteredEvents () {
      const keyword = String(this.searchText || '').trim().toLowerCase()

      return this.events.filter((event) => {
        if (this.scheduleScopeFilter !== 'all' && event.scheduleScope !== this.scheduleScopeFilter) return false
        if (this.statusFilter !== 'all' && event.scheduleScope !== 'console' && event.status !== this.statusFilter) return false
        if (this.visibilityFilter !== 'all' && event.scheduleScope === 'aone' && event.visibility !== this.visibilityFilter) return false
        if (this.partnerIdFilter && event.scheduleScope === 'partner' && Number(event.partnerId || 0) !== Number(this.partnerIdFilter)) return false

        if (!keyword) return true

        const target = [
          event.title,
          event.details,
          event.partnerName,
          event.categoryLabel,
          event.transportTypeLabel,
          event.mblNo,
          event.freightCode,
          event.departCity,
          event.arrivalCity
        ].filter(Boolean).join(' ').toLowerCase()

        return target.includes(keyword)
      })
    },
    selectedDateEvents () {
      return this.filteredEvents
        .filter((event) => this.isDateWithinEvent(this.selectedDate, event))
        .sort((a, b) => {
          const order = { console: 0, partner: 1, aone: 2 }
          if (order[a.scheduleScope] !== order[b.scheduleScope]) {
            return order[a.scheduleScope] - order[b.scheduleScope]
          }
          return String(a.title || '').localeCompare(String(b.title || ''), 'ko')
        })
    },
    scheduleSummary () {
      return {
        total: this.filteredEvents.length,
        console: this.filteredEvents.filter((event) => event.scheduleScope === 'console').length,
        partner: this.filteredEvents.filter((event) => event.scheduleScope === 'partner').length,
        aone: this.filteredEvents.filter((event) => event.scheduleScope === 'aone').length
      }
    },
    appliedFilters () {
      const filters = []
      const scope = this.scheduleScopeOptions.find((item) => item.value === this.scheduleScopeFilter)
      const status = this.statusOptions.find((item) => item.value === this.statusFilter)
      const visibility = this.visibilityOptions.find((item) => item.value === this.visibilityFilter)
      const partner = this.partnerFilterOptions.find((item) => Number(item.value) === Number(this.partnerIdFilter))

      if (this.searchText) filters.push({ key: 'searchText', label: `검색어: ${this.searchText}` })
      if (this.scheduleScopeFilter !== 'all' && scope) filters.push({ key: 'scheduleScopeFilter', label: `일정 구분: ${scope.label}` })
      if (this.statusFilter !== 'all' && status) filters.push({ key: 'statusFilter', label: `일정 상태: ${status.label}` })
      if (this.visibilityFilter !== 'all' && visibility) filters.push({ key: 'visibilityFilter', label: `공개 범위: ${visibility.label}` })
      if (this.partnerIdFilter && partner) filters.push({ key: 'partnerIdFilter', label: `파트너: ${partner.label}` })

      return filters
    }
  },
  watch: {
    selectedDate (value) {
      if (!value) return
      const nextDate = new Date(value)
      if (Number.isNaN(nextDate.getTime())) return
      if (
        nextDate.getFullYear() !== this.calDate.getFullYear() ||
        nextDate.getMonth() !== this.calDate.getMonth()
      ) {
        this.calDate = nextDate
      }
    }
  },
  methods: {
    formatDate (value) {
      if (!value) return ''
      return parseDate(new Date(value)).date
    },
    getMonthAnchorDate (date) {
      const nextDate = new Date(date)
      nextDate.setDate(1)
      return parseDate(nextDate).date
    },
    addMonths (date, months) {
      const nextDate = new Date(date)
      nextDate.setDate(1)
      nextDate.setMonth(nextDate.getMonth() + months)
      return nextDate
    },
    getScopeLabel (scope) {
      if (scope === 'console') return '자동 운송 일정'
      if (scope === 'partner') return '파트너 일정'
      if (scope === 'aone') return 'AONE 내부 일정'
      return '-'
    },
    getStatusLabel (status) {
      if (status === 'planned') return '예정'
      if (status === 'in_progress') return '진행 중'
      if (status === 'done') return '완료'
      if (status === 'on_hold') return '보류'
      return '-'
    },
    getVisibilityLabel (visibility) {
      if (visibility === 'private') return '개인 일정'
      if (visibility === 'shared') return '내부 공유'
      return '-'
    },
    getCategoryLabel (scope, category) {
      if (scope === 'partner') return PARTNER_CATEGORY_LABELS[category] || '기타'
      if (scope === 'aone') return AONE_CATEGORY_LABELS[category] || '기타'
      return '-'
    },
    getTransportTypeLabel (eventType) {
      const options = this.$store.getters.getFreightMethodList || []
      return options.find((item) => String(item.value) === String(eventType))?.label || '-'
    },
    getCurrentDay (day) {
      const newDay = new Date(this.calDate)
      newDay.setDate(day)
      return parseDate(newDay).date
    },
    isDateWithinEvent (date, event) {
      return date >= event.date && date <= event.endDate
    },
    getEvents (date) {
      return this.filteredEvents.filter((event) => this.isDateWithinEvent(date, event))
    },
    getEventColor (event) {
      if (event.scheduleScope === 'console') {
        return String(event.transportType || '') === '1' ? 'teal-6' : 'blue-6'
      }
      if (event.scheduleScope === 'partner') return 'orange-7'
      return event.visibility === 'private' ? 'grey-7' : 'deep-purple-6'
    },
    getEventIcon (event) {
      if (event.scheduleScope === 'console') {
        return String(event.transportType || '') === '1' ? 'directions_boat' : 'flight'
      }
      if (event.scheduleScope === 'partner') return 'storefront'
      return event.visibility === 'private' ? 'person' : 'groups'
    },
    badgeClasses (event) {
      return {
        'text-white': true,
        [`bg-${this.getEventColor(event)}`]: true,
        'schedule-badge': true
      }
    },
    badgeLabel (event) {
      if (event.scheduleScope === 'console') {
        return `${event.title} ${event.departCity || ''} → ${event.arrivalCity || ''}`.trim()
      }
      if (event.scheduleScope === 'partner') {
        return `${event.partnerName || '파트너'} · ${event.title}`
      }
      return event.title
    },
    mapEvents (payload = {}) {
      const consoleEvents = (payload.consoleSchedules || []).map((item) => ({
        id: item.id,
        scheduleScope: 'console',
        title: item.title,
        details: item.details,
        date: item.startDate,
        endDate: item.endDate,
        days: item.days,
        transportType: item.transportType,
        transportTypeLabel: this.getTransportTypeLabel(item.transportType),
        mblNo: item.mblNo,
        freightCode: item.freightCode,
        departCity: item.departCity,
        arrivalCity: item.arrivalCity,
        status: item.status,
        canManage: false
      }))

      const partnerEvents = (payload.partnerSchedules || []).map((item) => ({
        ...item,
        date: item.startDate,
        endDate: item.endDate,
        categoryLabel: this.getCategoryLabel('partner', item.scheduleCategory)
      }))

      const aoneEvents = (payload.aoneSchedules || []).map((item) => ({
        ...item,
        date: item.startDate,
        endDate: item.endDate,
        categoryLabel: this.getCategoryLabel('aone', item.scheduleCategory)
      }))

      this.events = [...consoleEvents, ...partnerEvents, ...aoneEvents]
    },
    async loadSchedule () {
      const payload = {
        year: this.calDate.getFullYear(),
        month: this.calDate.getMonth() + 1,
        scheduleScope: this.scheduleScopeFilter,
        status: this.statusFilter,
        visibility: this.visibilityFilter,
        partnerId: this.partnerIdFilter
      }

      const config = {
        method: 'POST',
        url: '/api/schedule/loadSchedule',
        data: payload,
        name: '[ Axios / 스케쥴 조회 ]'
      }

      await this.$http(config)
        .then((res) => {
          this.mapEvents(res.data || {})
        })
        .catch((err) => {
          this.$log.debug(err)
        })
    },
    addSchedule () {
      this.modalMode = 'add'
      this.selectedSchedule = {
        scheduleScope: this.isPartner ? 'partner' : 'partner',
        startDate: this.selectedDate,
        endDate: this.selectedDate,
        partnerId: this.isPartner ? this.session.partnerId : null,
        status: 'planned',
        visibility: 'shared'
      }
      this.modalVisibility = true
    },
    async reloadSchedule () {
      await this.loadSchedule()
    },
    async calendarNext () {
      const nextMonth = this.addMonths(this.calDate, 1)
      this.calDate = nextMonth
      this.selectedDate = this.getMonthAnchorDate(nextMonth)
      await this.loadSchedule()
    },
    async calendarPrev () {
      const prevMonth = this.addMonths(this.calDate, -1)
      this.calDate = prevMonth
      this.selectedDate = this.getMonthAnchorDate(prevMonth)
      await this.loadSchedule()
    },
    async calendarToday () {
      const today = new Date()
      this.calDate = today
      this.selectedDate = this.formatDate(today)
      await this.loadSchedule()
    },
    resetFilters () {
      this.scheduleScopeFilter = 'all'
      this.partnerIdFilter = null
      this.statusFilter = 'all'
      this.visibilityFilter = 'all'
      this.searchText = ''
    },
    removeFilter (key) {
      if (key === 'searchText') this.searchText = ''
      if (key === 'scheduleScopeFilter') this.scheduleScopeFilter = 'all'
      if (key === 'partnerIdFilter') this.partnerIdFilter = null
      if (key === 'statusFilter') this.statusFilter = 'all'
      if (key === 'visibilityFilter') this.visibilityFilter = 'all'
    },
    selectSchedule (event) {
      this.selectedSchedule = { ...event }
      this.modalMode = event.canManage ? 'modify' : 'view'
      this.modalVisibility = true
    }
  },
  async mounted () {
    this.$getFreightList()
    if (this.isAdmin) {
      await this.$getPartnerList()
    }
    await this.loadSchedule()
  }
}
</script>

<template>
  <div class="scheduler-page">
    <div class="comp-box schedule-control-box q-mb-md">
      <div class="month-navigator">
        <q-btn flat color="primary" icon="chevron_left" label="이전 달" @click="calendarPrev" />

        <div class="month-label-wrap">
          <div class="month-label">{{ currentMonthLabel }}</div>
          <div class="month-caption">자동 운송 일정, 파트너 일정, AONE 내부 일정을 한 화면에서 확인합니다.</div>
        </div>

        <q-btn flat color="primary" label="다음 달" icon-right="chevron_right" @click="calendarNext" />
      </div>

      <q-separator class="q-my-lg" />

      <div class="schedule-search-bar">
        <div class="row q-col-gutter-sm items-start">
          <div class="col-12 col-md">
            <q-input
              v-model="searchText"
              dense
              outlined
              clearable
              label="제목 / 설명 / MBL / 콘솔코드 / 파트너 검색"
            >
              <template #prepend>
                <q-icon name="search" />
              </template>
            </q-input>
          </div>
          <div class="col-12 col-md-auto">
            <q-btn color="grey-7" icon="restart_alt" label="초기화" flat @click="resetFilters" />
          </div>
        </div>

        <div class="search-detail-row q-mt-md">
          <div class="text-caption text-weight-bold text-grey-7 search-detail-label">상세 조건</div>
          <div class="row q-col-gutter-md items-start">
            <div class="col-12 col-md-3">
              <q-select
                v-model="scheduleScopeFilter"
                :options="scheduleScopeOptions"
                emit-value
                map-options
                dense
                outlined
                label="일정 구분"
              />
            </div>
            <div v-if="isAdmin" class="col-12 col-md-3">
              <q-select
                v-model="partnerIdFilter"
                :options="partnerFilterOptions"
                emit-value
                map-options
                dense
                outlined
                label="파트너"
                clearable
              />
            </div>
            <div class="col-12 col-md-3">
              <q-select
                v-model="statusFilter"
                :options="statusOptions"
                emit-value
                map-options
                dense
                outlined
                label="일정 상태"
              />
            </div>
            <div v-if="isAdmin" class="col-12 col-md-3">
              <q-select
                v-model="visibilityFilter"
                :options="visibilityOptions"
                emit-value
                map-options
                dense
                outlined
                label="AONE 공개 범위"
              />
            </div>
          </div>
        </div>

        <div v-if="appliedFilters.length" class="applied-filter-row q-mt-md">
          <span class="text-caption text-grey-7">적용 조건</span>
          <q-chip
            v-for="filter in appliedFilters"
            :key="filter.key"
            removable
            color="blue-1"
            text-color="primary"
            @remove="removeFilter(filter.key)"
          >
            {{ filter.label }}
          </q-chip>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md q-mb-md">
      <div class="col-12 col-md-3">
        <div class="comp-box summary-box">
          <div class="summary-label">전체 일정</div>
          <div class="summary-value">{{ scheduleSummary.total }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box summary-box">
          <div class="summary-label">자동 운송 일정</div>
          <div class="summary-value">{{ scheduleSummary.console }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box summary-box">
          <div class="summary-label">파트너 일정</div>
          <div class="summary-value">{{ scheduleSummary.partner }}</div>
        </div>
      </div>
      <div v-if="isAdmin" class="col-12 col-md-3">
        <div class="comp-box summary-box">
          <div class="summary-label">AONE 내부 일정</div>
          <div class="summary-value">{{ scheduleSummary.aone }}</div>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md items-stretch">
      <div class="col-12 col-lg-8">
        <div class="calendar-card">
          <QCalendarMonth v-model="selectedDate" view="month" locale="ko-KR" :day-height="108">
            <template #day="{ scope: { timestamp } }">
              <div class="day-cell">
                <template v-for="(event, index) in getEvents(timestamp.date)" :key="event.id ?? `${timestamp.date}-${index}`">
                  <q-badge :class="badgeClasses(event)" @click="selectSchedule(event)">
                    <q-icon v-if="getEventIcon(event)" :name="getEventIcon(event)" size="12px" class="q-mr-xs" />
                    <span class="ellipsis">{{ badgeLabel(event) }}</span>
                    <q-tooltip>{{ event.details }}</q-tooltip>
                  </q-badge>
                </template>
              </div>
            </template>
          </QCalendarMonth>
        </div>
      </div>

      <div class="col-12 col-lg-4">
        <div class="agenda-card">
          <div class="agenda-head">
            <div class="agenda-title">선택 날짜 일정</div>
            <div class="agenda-date">{{ selectedDate }}</div>
          </div>

          <div v-if="selectedDateEvents.length === 0" class="empty-state">
            <q-icon name="event_busy" size="32px" class="q-mb-sm" />
            <div>선택한 날짜에 일정이 없습니다.</div>
          </div>

          <q-list v-else separator>
            <q-item
              v-for="event in selectedDateEvents"
              :key="`${event.scheduleScope}-${event.id}`"
              clickable
              class="agenda-item"
              @click="selectSchedule(event)"
            >
              <q-item-section avatar>
                <q-avatar :color="getEventColor(event)" text-color="white" :icon="getEventIcon(event)" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-weight-bold">{{ event.title }}</q-item-label>
                <q-item-label caption>{{ event.details }}</q-item-label>
                <q-item-label caption>
                  {{ getScopeLabel(event.scheduleScope) }} ·
                  {{ event.scheduleScope === 'console' ? event.transportTypeLabel : event.categoryLabel }} ·
                  {{ event.startDate }} ~ {{ event.endDate }}
                </q-item-label>
                <q-item-label v-if="event.scheduleScope === 'partner'" caption>
                  {{ event.partnerName || '-' }} · {{ getStatusLabel(event.status) }}
                </q-item-label>
                <q-item-label v-if="event.scheduleScope === 'aone'" caption>
                  {{ getVisibilityLabel(event.visibility) }} · {{ getStatusLabel(event.status) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
      </div>
    </div>

    <teleport to="body">
      <q-dialog v-model="modalVisibility">
        <Modal
          :selected="selectedSchedule"
          :mode="modalMode"
          @reload-schedule="reloadSchedule"
          @close-modal="modalVisibility = false"
        />
      </q-dialog>
    </teleport>
  </div>
</template>

<style scoped lang="scss">
.scheduler-page {
  display: flex;
  flex-direction: column;
}

.calendar-card,
.agenda-card {
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  border: 1px solid #dbe3ef;
  border-radius: 18px;
  box-shadow: 0 14px 34px rgba(13, 35, 67, 0.08);
}

.schedule-control-box {
  padding: 20px 22px;
}

.month-navigator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.month-label-wrap {
  flex: 1 1 auto;
  text-align: center;
}

.month-label {
  font-size: 26px;
  font-weight: 700;
  color: #17324d;
  letter-spacing: -0.02em;
}

.month-caption {
  margin-top: 4px;
  font-size: 12px;
  color: #63788d;
}

.schedule-search-bar {
  padding-top: 18px;
}

.search-detail-row {
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.search-detail-label {
  display: block;
  margin-bottom: 8px;
}

.applied-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.summary-box {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 110px;
}

.summary-label {
  font-size: 12px;
  color: #62788f;
}

.summary-value {
  font-size: 24px;
  font-weight: 700;
  color: #102942;
}

.calendar-card,
.agenda-card {
  height: 100%;
  padding: 16px;
}

.day-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 88px;
}

.schedule-badge {
  width: 100%;
  cursor: pointer;
  justify-content: flex-start;
  min-height: 20px;
  padding: 2px 6px;
}

.agenda-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.agenda-title {
  font-size: 18px;
  font-weight: 700;
  color: #17324d;
}

.agenda-date {
  font-size: 13px;
  color: #5f7388;
}

.agenda-item {
  border-radius: 12px;
}

.empty-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6b7b8c;
}

@media (max-width: 1023px) {
  .month-label-wrap {
    order: -1;
  }

  .search-detail-row {
    padding-top: 12px;
  }

  .month-navigator {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
